import * as Application from 'expo-application'
import Constants from 'expo-constants'
import * as Crypto from 'expo-crypto'
import * as Device from 'expo-device'
import * as Localization from 'expo-localization'
import { Platform } from 'react-native'
import { isIsolatedPreviewBuild } from '../runtime-build'
import { supabase } from '../supabase'
import { SecureStorageAdapter } from '../storage/secure-storage'

type AuthSecurityEvent = 'initial_session' | 'signed_in' | 'token_refreshed' | 'user_updated'

type ContactSecurityEvent =
  | 'contact_verification_required'
  | 'contact_verification_send_requested'
  | 'contact_verification_sent'
  | 'contact_verification_send_failed'
  | 'contact_verification_verify_requested'
  | 'contact_verification_verified'
  | 'contact_verification_verify_failed'

type ContactSecurityPayload = {
  event: ContactSecurityEvent
  userId?: string | null
  contactType: 'email' | 'phone'
  flow: 'email_link' | 'phone_change' | 'phone_otp'
  channel?: 'sms' | 'whatsapp' | 'email' | null
  target?: string | null
  operationId?: string | null
  error?: unknown
}

const INSTALLATION_ID_KEY = 'tago.security.installation_id'
const TOKEN_REFRESH_MIN_INTERVAL_MS = 6 * 60 * 60 * 1000
const AUTH_EVENT_DEDUPE_MS = 5 * 60 * 1000
const CONTACT_REQUIRED_DEDUPE_MS = 5 * 60 * 1000

let runtimeSessionId: string | null = null
let runtimeUserId: string | null = null
let installationIdPromise: Promise<string> | null = null
let lastTokenRefreshEventAt = 0
let recentSecurityEventAt = new Map<string, number>()

function randomId(): string {
  if (typeof Crypto.randomUUID === 'function') return Crypto.randomUUID()
  return Array.from(Crypto.getRandomBytes(16))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('')
}

async function installationId(): Promise<string> {
  if (installationIdPromise) return installationIdPromise

  installationIdPromise = (async () => {
    const existing = await SecureStorageAdapter.getItem(INSTALLATION_ID_KEY)
    if (existing) return existing

    const next = `install-${randomId()}`
    await SecureStorageAdapter.setItem(INSTALLATION_ID_KEY, next)
    return next
  })()

  return installationIdPromise
}

function sessionIdFor(userId: string): string {
  if (runtimeSessionId && runtimeUserId === userId) return runtimeSessionId
  runtimeUserId = userId
  runtimeSessionId = `session-${Date.now().toString(36)}-${randomId()}`
  return runtimeSessionId
}

function shouldSkipRecentEvent(key: string, windowMs: number): boolean {
  const now = Date.now()
  const lastSeen = recentSecurityEventAt.get(key) ?? 0
  if (now - lastSeen < windowMs) return true

  recentSecurityEventAt.set(key, now)
  if (recentSecurityEventAt.size > 200) {
    for (const [eventKey, eventAt] of recentSecurityEventAt) {
      if (now - eventAt > AUTH_EVENT_DEDUPE_MS) recentSecurityEventAt.delete(eventKey)
    }
  }
  return false
}

async function devicePayload(provider?: string | null): Promise<Record<string, unknown>> {
  const deviceType = await Device.getDeviceTypeAsync().catch(() => null)

  return {
    appOwnership: Constants.appOwnership ?? null,
    appVersion: Application.nativeApplicationVersion ?? Constants.expoConfig?.version ?? null,
    deviceId: await installationId(),
    deviceName: Device.deviceName ?? null,
    deviceType,
    locale: Localization.getLocales()[0]?.languageTag ?? null,
    nativeBuildVersion: Application.nativeBuildVersion ?? null,
    osName: Device.osName ?? Platform.OS,
    osVersion: Device.osVersion ?? null,
    platform: Platform.OS,
    provider,
    runtimeVersion: Constants.expoConfig?.runtimeVersion ?? null,
    timezone:
      typeof Intl !== 'undefined'
        ? Intl.DateTimeFormat().resolvedOptions().timeZone
        : (Localization.getCalendars()[0]?.timeZone ?? null),
  }
}

function errorCode(error: unknown): string | null {
  if (!error || typeof error !== 'object') return null
  const item = error as { code?: unknown; status?: unknown; name?: unknown }
  if (typeof item.code === 'string' && item.code) return item.code.slice(0, 80)
  if (typeof item.code === 'number') return String(item.code)
  if (typeof item.status === 'number') return `status_${item.status}`
  if (typeof item.name === 'string' && item.name) return item.name.slice(0, 80)
  return null
}

function errorStatus(error: unknown): number | null {
  if (!error || typeof error !== 'object') return null
  const status = (error as { status?: unknown; statusCode?: unknown }).status
  const statusCode = (error as { statusCode?: unknown }).statusCode
  if (typeof status === 'number' && Number.isFinite(status)) return status
  if (typeof statusCode === 'number' && Number.isFinite(statusCode)) return statusCode
  return null
}

function normalizeContactTarget(value: string, type: 'email' | 'phone'): string {
  const trimmed = value.trim().toLowerCase()
  if (type === 'phone') return trimmed.replace(/[^\d+]/g, '')
  return trimmed
}

async function contactFingerprint(
  value: string | null | undefined,
  type: 'email' | 'phone',
): Promise<string | null> {
  if (!value?.trim()) return null
  const normalized = normalizeContactTarget(value, type)
  if (!normalized) return null
  const digest = await Crypto.digestStringAsync(
    Crypto.CryptoDigestAlgorithm.SHA256,
    `${type}:${normalized}`,
  )
  return `${type}:${digest.slice(0, 48)}`
}

export const securityTelemetryService = {
  resetRuntimeSession(): void {
    runtimeSessionId = null
    runtimeUserId = null
    lastTokenRefreshEventAt = 0
    recentSecurityEventAt = new Map<string, number>()
  },

  recordAuthEvent(event: AuthSecurityEvent, userId: string, provider?: string | null): void {
    // Preview is an isolated, disposable fixture application. Security events
    // are operational telemetry, not part of the user journey, so never make
    // its local QA sessions depend on (or pollute) an Edge Function deployment.
    if (isIsolatedPreviewBuild) return

    if (event === 'token_refreshed') {
      const now = Date.now()
      if (now - lastTokenRefreshEventAt < TOKEN_REFRESH_MIN_INTERVAL_MS) return
      lastTokenRefreshEventAt = now
    }

    const sessionId = sessionIdFor(userId)
    if (
      event !== 'token_refreshed' &&
      shouldSkipRecentEvent(
        `auth:${event}:${userId}:${provider ?? ''}:${sessionId}`,
        AUTH_EVENT_DEDUPE_MS,
      )
    ) {
      return
    }

    void (async () => {
      const device = await devicePayload(provider)
      await supabase.functions.invoke('record-security-event', {
        body: {
          event,
          sessionId,
          device,
        },
      })
    })().catch((error) => {
      if (__DEV__) console.warn('[securityTelemetry] record failed:', error)
    })
  },

  recordContactVerificationEvent(payload: ContactSecurityPayload): void {
    if (isIsolatedPreviewBuild) return

    void (async () => {
      const session = await supabase.auth.getSession().catch(() => null)
      const userId = payload.userId ?? session?.data.session?.user.id ?? null
      if (!userId) return

      const sessionId = sessionIdFor(userId)
      if (
        payload.event === 'contact_verification_required' &&
        shouldSkipRecentEvent(
          `contact-required:${userId}:${payload.contactType}:${payload.flow}:${sessionId}`,
          CONTACT_REQUIRED_DEDUPE_MS,
        )
      ) {
        return
      }

      const device = await devicePayload()
      const operationId = payload.operationId ?? `contact-${Date.now().toString(36)}`
      await supabase.functions.invoke('record-security-event', {
        body: {
          event: payload.event,
          sessionId,
          device,
          contact: {
            type: payload.contactType,
            flow: payload.flow,
            channel: payload.channel ?? null,
            operationId,
            targetFingerprint: await contactFingerprint(payload.target, payload.contactType),
            errorCode: errorCode(payload.error),
            errorStatus: errorStatus(payload.error),
          },
        },
      })
    })().catch((error) => {
      if (__DEV__) console.warn('[securityTelemetry] contact record failed:', error)
    })
  },
}
